import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { toast } from "sonner";
import { History, Volume2, VolumeX } from "lucide-react";
import { api, errMsg, fmt } from "@/lib/api";
import { unlockAudio, playTick, playWin, playLose, isSoundOn, setSoundOn } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import { SlideCard, HistoryPill } from "@/components/game/SlideCard";
import { BetControls } from "@/components/game/BetControls";

const GAP = 12;
const START = 6;
const randStrip = () =>
  Array.from({ length: 60 }, () => {
    const r = Math.random();
    return Number((r < 0.7 ? 1 + Math.random() : r < 0.9 ? 2 + Math.random() * 2 : r < 0.97 ? 4 + Math.random() * 5 : 10 + Math.random() * 20).toFixed(2));
  });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const SlideGame = ({ onRound }) => {
  const { user, setBalance } = useAuth();
  const wrapRef = useRef(null);
  const [w, setW] = useState(0);
  const [strip, setStrip] = useState(randStrip);
  const [center, setCenter] = useState(START);
  const [active, setActive] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);
  const [threshold, setThreshold] = useState(null);
  const [betAmount, setBetAmount] = useState(0);
  const controls = useAnimation();
  const [sound, setSound] = useState(isSoundOn);
  const lastIdx = useRef(null);
  const sliding = useRef(false);
  const small = w < 640;
  const CW = small ? 92 : 112;
  const CH = small ? 134 : 160;
  const STEP = CW + GAP;
  const posFor = useCallback((i, j = 0) => w / 2 - (i * STEP + CW / 2) + j, [w, STEP, CW]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (w && phase !== "sliding") controls.set({ x: posFor(center) });
  }, [w, CW]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/game/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/game/config").then((r) => setThreshold(r.data.win_threshold)).catch(() => {});
  }, []);

  const onUpdate = (latest) => {
    if (!sliding.current) return;
    const idx = Math.round((w / 2 - CW / 2 - Number(latest.x)) / STEP);
    if (idx !== lastIdx.current) {
      lastIdx.current = idx;
      playTick();
    }
  };

  const play = async (amount) => {
    unlockAudio();
    let data;
    try {
      ({ data } = await api.post("/game/bet", { amount }));
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
    setBetAmount(amount);
    setBalance(Math.round((data.balance - data.payout) * 100) / 100);
    setActive(null);
    setPhase("sliding");
    setStrip(data.strip);
    controls.set({ x: posFor(START) });
    await new Promise((r) => requestAnimationFrame(r));
    const jitter = (Math.random() - 0.5) * CW * 0.7;
    lastIdx.current = START;
    sliding.current = true;
    await controls.start({ x: posFor(data.target_index, jitter), transition: { duration: 4.2, ease: [0.12, 0.72, 0.18, 1] } });
    sliding.current = false;
    if (data.win) playWin(); else playLose();
    setCenter(data.target_index);
    setActive(data.target_index);
    setPhase("done");
    setLast(data);
    setBalance(data.balance);
    setHistory((h) => [data, ...h].slice(0, 20));
    onRound?.(data);
    await sleep(900);
    return true;
  };

  const status =
    phase === "sliding"
      ? "Kayıyor..."
      : phase === "done" && last
        ? last.win
          ? `Kazandınız ₺${fmt(last.payout)}`
          : `Kaybettiniz · ${fmt(last.multiplier)}×`
        : "Bahis yapmaya hazır";

  return (
    <div className="grid lg:grid-cols-[320px_1fr] rounded-xl overflow-hidden bg-[var(--x-panel)] shadow-[0_10px_40px_-12px_rgba(0,0,0,.6)]" data-testid="slide-game">
      <div className="order-2 lg:order-1 p-4 bg-[var(--x-panel)]">
        <BetControls busy={phase === "sliding"} play={play} balance={user?.balance || 0} />
      </div>
      <div className="order-1 lg:order-2 bg-[#141823] flex flex-col min-w-0">
        <div className="flex items-center gap-2 p-3 sm:p-4 overflow-x-auto no-scrollbar" data-testid="history-bar">
          {history.slice(0, 8).map((h, i) => (
            <HistoryPill key={h.id} m={h.multiplier} win={h.win} testId={`history-pill-${i}`} />
          ))}
          {history.length === 0 && <div className="text-sm text-[var(--x-dim)] px-2 py-2">Henüz tur yok</div>}
          <button
            data-testid="sound-toggle-btn"
            onClick={() => { setSoundOn(!sound); setSound(!sound); unlockAudio(); }}
            className="ml-auto shrink-0 w-10 h-10 rounded-full bg-[#2f3547] hover:bg-[#3d4357] transition-colors flex items-center justify-center"
            title={sound ? "Sesi kapat" : "Sesi aç"}
          >
            {sound ? <Volume2 size={18} /> : <VolumeX size={18} className="text-[var(--x-muted)]" />}
          </button>
          <div className="shrink-0 w-10 h-10 rounded-full bg-[#2f3547] flex items-center justify-center"><History size={18} /></div>
        </div>
        <div className="flex justify-center">
          <div className="rounded-full bg-[#232838] px-6 py-2.5 flex items-center gap-2 font-extrabold text-lg" data-testid="my-bet-pill">
            Bahislerim <span className="text-[var(--x-green)] tabular-nums">₺{fmt(phase === "idle" ? 0 : betAmount)}</span>
          </div>
        </div>
        <div ref={wrapRef} className="relative overflow-hidden mt-6 sm:mt-10" style={{ height: CH + 70 }}>
          <motion.div animate={controls} initial={false} onUpdate={onUpdate} className="absolute top-3 left-0 flex" style={{ gap: GAP }}>
            {strip.map((m, i) => (
              <SlideCard key={i} m={m} w={CW} h={CH} active={active === i} dim={active !== null && active !== i} />
            ))}
          </motion.div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 bg-gradient-to-r from-[#141823] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 bg-gradient-to-l from-[#141823] to-transparent" />
          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex flex-col items-center" style={{ top: CH - 18 }}>
            <div className="w-[3px] h-12 bg-white rounded" />
            <div className="w-6 h-6 rounded-full border-[3px] border-white bg-[#141823] -mt-0.5" />
          </div>
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 pb-5 pt-1 text-[15px]">
          <div className="text-[var(--x-muted)] font-semibold text-[13px]">
            {threshold && <>Kazanç: <b className="text-white">{fmt(threshold)}×</b> ve üzeri</>}
          </div>
          <div data-testid="round-status" className={`font-bold text-[13px] sm:text-[14px] whitespace-nowrap ${phase === "done" && last ? (last.win ? "text-[var(--x-green)]" : "text-[var(--x-red)]") : ""}`}>{status}</div>
          <div className="flex items-center gap-2 text-[var(--x-muted)] font-semibold text-[13px]">
            Ağ <span className="w-2.5 h-2.5 rounded-full bg-[var(--x-green)] x-pulse-dot" />
          </div>
        </div>
      </div>
    </div>
  );
};
